import React from "react";
import styled from "styled-components";
import { Alert, Form, Button } from "react-bootstrap";
import {
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  AppFlowLoginButton,
  BackNavRow,
  PageContainer,
  SchoolLoginButton,
  SchoolLoginButtonIconImage,
  AppLink,
} from "../common/CommonStyles";
import { t } from "i18next";
import {
  AccountContext,
  RetryBehavior,
} from "../common/context/AccountContext";
import { CLIENT_TYPE, HTTP_BAD_REQUEST } from "../common/api";
import { CLEVER_REDIRECT_PATH, CLASSLINK_REDIRECT_PATH } from "../common/utils";
import { useHistory, useLocation } from "react-router-dom";
import { RetryContext } from "../common/context/RetryContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import {
  AnalyticsContext,
  LoginWithClassLinkButtonTapEvent,
  LoginWithClassLinkFailedEvent,
  LoginWithClassLinkSuccessfulEvent,
  LoginWithCleverButtonTapEvent,
  LoginWithCleverFailedEvent,
  LoginWithCleverSuccessfulEvent,
  ViewLogInEvent,
} from "../common/context/AnalyticsContext";
import { ApiContext } from "../common/context/ApiContext";
import { EMAIL_ADDRESS_REGEX } from "../common/utils";
import {
  COLOR_TEXT_PRIMARY,
  COLOR_GRAY_DISABLED,
  COLOR_RED_LOGIN,
  COLOR_SURFACE_CONTAINER_ALPHA,
} from "../common/colors";

export const ErrorText = styled.div`
  color: ${COLOR_RED_LOGIN};
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 12px;
  text-align: center;
  white-space: pre-line;
`;

const maybeExtractKeyFromJson = (
  key: string,
  json: string | null
): string | null => {
  if (json !== null) {
    try {
      const value = JSON.parse(json);
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return value[key];
      }
    } catch {
      // ignore
    }
  }
  return null;
};

// SSO codes already sent to the backend during this page load. A code is single-use, but the
// login effect below re-runs whenever its dependencies change (and LoginPage can remount while
// the account loads), which used to POST the same code again: the duplicate fails with
// invalid_grant and could show an error after the first request had logged the user in.
const postedSsoCodes = new Set<string>();

export const LoginPage: React.FC = () => {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const { makeRequest } = React.useContext(AccountContext);
  const { login, loginWithClever, loginWithClassLink } =
    React.useContext(AccountContext);
  const history = useHistory();
  const { showRetry } = React.useContext(RetryContext);
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const { logEvent } = React.useContext(AnalyticsContext);
  const { resetPassword } = React.useContext(ApiContext);
  const location = useLocation();
  const isCleverLoginPage = window.location.pathname === CLEVER_REDIRECT_PATH;
  const isClassLinkLoginPage =
    window.location.pathname === CLASSLINK_REDIRECT_PATH;
  const [isForgotPasswordDialogOpen, setIsForgotPasswordDialogOpen] =
    React.useState<boolean>(false);
  const forgotPasswordEmailFieldRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    logEvent(new ViewLogInEvent());
  }, [logEvent]);
  const clearErrors = React.useCallback(() => {
    setError("");
    setEmailError("");
    setPasswordError("");
  }, []);

  const setErrors = React.useCallback((errorBody: any) => {
    setEmailError(errorBody?.email?.[0] || "");
    setPasswordError(errorBody?.password?.[0] || "");
    setError(
      errorBody?.error_message?.[0] || errorBody?.non_field_errors?.[0] || ""
    );
  }, []);

  React.useEffect(() => {
    const maybeLoginWithClever = async () => {
      const params = new URLSearchParams(window.location.search);
      if (window.location.pathname !== CLEVER_REDIRECT_PATH) {
        return;
      }
      const redirect_uri =
        params.get("redirect_uri") ||
        window.location.origin + CLEVER_REDIRECT_PATH;
      const code = params.get("code");
      if (code !== null && !postedSsoCodes.has(code)) {
        postedSsoCodes.add(code);
        const isSsoRetry =
          maybeExtractKeyFromJson("sso_retry", params.get("state")) === "1";
        startLoading();
        const loginResponse = await makeRequest(
          "auth/clever/login/",
          "POST",
          RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
          true,
          {
            code,
            redirect_uri,
            client_type: CLIENT_TYPE,
            sso_retry: isSsoRetry,
          }
        );
        if (loginResponse.body) {
          stopLoading();
          login(loginResponse.body.access_token, loginResponse.body.user);
          logEvent(new LoginWithCleverSuccessfulEvent());
          history.replace(CLEVER_REDIRECT_PATH);
          // If SSO provider sent us "state" JSON object with "next" in it, that means we initiated
          // this login ourselves with a predetermined destination that we should redirect to
          const nextDestination = maybeExtractKeyFromJson(
            "next",
            params.get("state")
          );
          history.push(nextDestination ?? location.state?.next ?? "/", {
            fromLogin: true,
          });
        } else {
          logEvent(new LoginWithCleverFailedEvent());
          const errorCode = loginResponse.errorBody?.error_code;
          if (
            !isSsoRetry &&
            (errorCode === "sso_code_rejected" ||
              errorCode?.[0] === "sso_code_rejected")
          ) {
            // Clever rejected the code (already redeemed or expired, e.g. an old ?code= URL
            // reopened from browser history). Restart OAuth once: if the Clever session is still
            // alive, Clever sends back a fresh code without the student seeing an error.
            loginWithClever({
              next: maybeExtractKeyFromJson("next", params.get("state")),
              ssoRetry: true,
            });
            return;
          }
          history.replace(CLEVER_REDIRECT_PATH);
          stopLoading();
          setErrors({
            error_message: loginResponse.errorBody?.error_message || [
              t("login_provider_error", { provider: "Clever" }),
            ],
          });
        }
      }
    };

    const maybeLoginWithClassLink = async () => {
      const params = new URLSearchParams(window.location.search);
      if (window.location.pathname !== CLASSLINK_REDIRECT_PATH) {
        return;
      }
      const redirect_uri =
        params.get("redirect_uri") ||
        window.location.origin + CLASSLINK_REDIRECT_PATH;
      const code = params.get("code");
      if (code !== null && !postedSsoCodes.has(code)) {
        postedSsoCodes.add(code);
        const isSsoRetry =
          maybeExtractKeyFromJson("sso_retry", params.get("state")) === "1";
        startLoading();
        const loginResponse = await makeRequest(
          "auth/classlink/login/",
          "POST",
          RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
          true,
          {
            code,
            redirect_uri,
            client_type: CLIENT_TYPE,
            sso_retry: isSsoRetry,
          }
        );
        if (loginResponse.body) {
          stopLoading();
          login(loginResponse.body.access_token, loginResponse.body.user);
          logEvent(new LoginWithClassLinkSuccessfulEvent());
          history.replace(CLASSLINK_REDIRECT_PATH);
          // If SSO provider sent us "state" JSON object with "next" in it, that means we initiated
          // this login ourselves with a predetermined destination that we should redirect to
          const nextDestination = maybeExtractKeyFromJson(
            "next",
            params.get("state")
          );
          history.push(nextDestination ?? location.state?.next ?? "/", {
            fromLogin: true,
          });
        } else {
          logEvent(new LoginWithClassLinkFailedEvent());
          const errorCode = loginResponse.errorBody?.error_code;
          if (
            !isSsoRetry &&
            (errorCode === "sso_code_rejected" ||
              errorCode?.[0] === "sso_code_rejected")
          ) {
            // ClassLink rejected the code (already redeemed or expired, e.g. the app already used
            // it, or an old ?code= URL reopened from browser history). Restart OAuth once: if the
            // ClassLink session is still alive, ClassLink sends back a fresh code without the
            // student seeing an error.
            loginWithClassLink({
              next: maybeExtractKeyFromJson("next", params.get("state")),
              ssoRetry: true,
            });
            return;
          }
          history.replace(CLASSLINK_REDIRECT_PATH);
          stopLoading();
          setErrors({
            error_message: loginResponse.errorBody?.error_message || [
              t("login_provider_error", { provider: "ClassLink" }),
            ],
          });
        }
      }
    };

    maybeLoginWithClever();
    maybeLoginWithClassLink();
  }, [
    history,
    makeRequest,
    login,
    loginWithClever,
    loginWithClassLink,
    setErrors,
    startLoading,
    stopLoading,
    showRetry,
    logEvent,
    location,
  ]);

  const loginClicked = React.useCallback(async () => {
    clearErrors();
    startLoading();
    const loginResponse = await makeRequest(
      "auth/login/",
      "POST",
      RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
      false,
      { email, password, client_type: CLIENT_TYPE }
    );
    if (loginResponse.body) {
      stopLoading();
      login(loginResponse.body.access_token, loginResponse.body.user);
      history.push(location.state?.next ?? "/", { fromLogin: true });
    } else if (loginResponse.code === HTTP_BAD_REQUEST) {
      stopLoading();
      setErrors(loginResponse.errorBody);
    } else {
      showRetry(() => {
        loginClicked();
      });
    }
  }, [
    clearErrors,
    email,
    history,
    login,
    makeRequest,
    password,
    setErrors,
    showRetry,
    startLoading,
    stopLoading,
    location,
  ]);

  const onForgotPasswordSubmit = React.useCallback(async () => {
    if (forgotPasswordEmailFieldRef.current) {
      const email = forgotPasswordEmailFieldRef.current.value;
      forgotPasswordEmailFieldRef.current.value = "";
      setIsForgotPasswordDialogOpen(false);
      if (email === "" || !email.match(EMAIL_ADDRESS_REGEX)) {
        return;
      }
      resetPassword(email);
    }
  }, [resetPassword, setIsForgotPasswordDialogOpen]);

  return (
    <PageContainer>
      <BackNavRow label={t("generic_nav_back")}>
        <div className="text-center">
          <h2>{t("login_title")}</h2>
          <p>
            <AppLink
              key={"forgot-password-link"}
              to=""
              onClick={(e) => {
                e.preventDefault();
                setIsForgotPasswordDialogOpen(true);
              }}
              style={{
                display: "block",
              }}
            >
              {t("login_forgot_password")}
            </AppLink>
          </p>
        </div>
      </BackNavRow>
      {location.state?.message && (
        <Alert variant="warning">{location.state.message}</Alert>
      )}
      {error && <ErrorText>{error}</ErrorText>}
      <Form
        className="text-center"
        onSubmit={(e) => {
          e.preventDefault();
          loginClicked();
        }}
      >
        <div className="text-center">
          <FormControl sx={{ m: 1, minWidth: 300 }}>
            <TextField
              id="email"
              label={t("login_email_placeholder")}
              type="email"
              required={true}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError !== ""}
              helperText={emailError}
              sx={{ backgroundColor: COLOR_SURFACE_CONTAINER_ALPHA }}
            />
          </FormControl>
        </div>

        <div className="text-center">
          <FormControl sx={{ m: 1, minWidth: 300 }}>
            <TextField
              id="password"
              label={t("login_password_placeholder")}
              type="password"
              required={true}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError !== ""}
              helperText={passwordError}
              sx={{ backgroundColor: COLOR_SURFACE_CONTAINER_ALPHA }}
            />
          </FormControl>
        </div>

        <AppFlowLoginButton
          disabled={!email || !password}
          isEnabled={!!email && !!password}
        >
          {t("login_continue")}
        </AppFlowLoginButton>
      </Form>
      <div className="text-center">
        <hr
          style={{
            marginTop: 20,
            marginLeft: "auto",
            marginRight: "auto",
            marginBottom: 10,
            width: 300,
            color: COLOR_TEXT_PRIMARY,
          }}
        />
        <p style={{ color: COLOR_GRAY_DISABLED }}>{t("login_or")}</p>
        {!isClassLinkLoginPage && (
          <SchoolLoginButton
            disabled={false}
            onClick={() => {
              logEvent(new LoginWithCleverButtonTapEvent());
              loginWithClever();
            }}
          >
            <SchoolLoginButtonIconImage
              src={`${process.env.PUBLIC_URL}/clever.png`}
              fluid
            />
            {t("login_with_provider", { provider: "Clever" })}
          </SchoolLoginButton>
        )}
        {!isCleverLoginPage && (
          <SchoolLoginButton
            disabled={false}
            onClick={() => {
              logEvent(new LoginWithClassLinkButtonTapEvent());
              loginWithClassLink();
            }}
          >
            <SchoolLoginButtonIconImage
              src={`${process.env.PUBLIC_URL}/classlink.png`}
              fluid
            />
            {t("login_with_provider", { provider: "ClassLink" })}
          </SchoolLoginButton>
        )}
      </div>
      <div style={{ paddingBottom: 40 }} />
      <Dialog
        open={isForgotPasswordDialogOpen}
        onClose={() => setIsForgotPasswordDialogOpen(false)}
        aria-labelledby="forgot-password-dialog-title"
        aria-describedby="forgot-password-dialog-description"
      >
        <DialogTitle id="forgot-password-dialog-title">
          {t("login_forgot_password")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="forgot-password-dialog-description">
            {t("login_forgot_password_dialog_description")}
          </DialogContentText>
          <TextField
            inputRef={forgotPasswordEmailFieldRef}
            autoFocus
            margin="dense"
            label={t("login_email_placeholder")}
            fullWidth
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.preventDefault();
              onForgotPasswordSubmit();
            }}
          >
            {t("generic_submit")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};
